import {
  LegalAddress,
  LegalDocument,
  LegalItem,
  LegalLink,
  LegalList,
  LegalSection,
  LegalStrong,
  LegalSubheading,
  LegalText,
} from '../ui/LegalDocument';
import { PRIVACY_POLICY_VERSION } from '../lib/legalDocuments';

/**
 * The privacy policy — a real document, reachable signed out, at `/privacy` (TROOP-203).
 *
 * <b>Every claim here was checked against the code that makes it true, not against the design
 * docs.</b> That is the whole substance of this ticket: a policy that overstates protection is
 * worse than none, and the failure mode is the drifted-count one with a regulator attached. The
 * mapping, so the next reader can re-check rather than re-derive:
 *
 * <ul>
 *   <li><b>No analytics or advertising</b> — `web/package.json` has five runtime dependencies and
 *     none of them is a tracker; `index.html` loads one inline theme script and the app bundle;
 *     `fonts.css` serves both faces from `/fonts`. TROOP-207 records that there is no error
 *     monitoring either, which is a gap there and a true sentence here. <b>"No third-party scripts"
 *     was the first draft and was false</b>: `lib/google.ts` injects `accounts.google.com/gsi/client`
 *     into the head, `SignInScreen` calls it from a `useEffect` as soon as the server reports Google
 *     configured, and the reader has chosen nothing at that point. Checking `index.html` and
 *     `package.json` is what missed it — a script added at runtime is in neither.</li>
 *   <li><b>A young person's phone number is never collected</b> — `RosterService.SetPhoneAsync`
 *     refuses anybody not classified `Adult`, and `Person.PhoneNumber`'s own remarks say why (rule
 *     10). <b>There was a second writer and this ticket closed it</b>: `AddAdultAsync`'s reuse
 *     branch wrote a number onto whoever the address resolved to, which since TROOP-224 can be a
 *     child, because their address is what their login is made of. That branch now refuses
 *     anybody not classified adult before it reaches the number (TROOP-245).</li>
 *   <li><b>A young person's address is a guardian's to set</b> — `RosterService.SetEmailAsync`
 *     refuses a non-adult on the roster route, so `/family` is the only door (rules 10 and 12).</li>
 *   <li><b>Location is stripped server-side</b> — `ImageIngest.ProcessAsync` re-encodes and
 *     `StripMetadata` clears the descriptive blocks, EXIF, XMP, IPTC, the colour profile and a
 *     PNG's text, pinned per format by `No_metadata_block_survives_ingest` and, for the text,
 *     `A_pngs_text_chunks_do_not_survive_ingest`. It leaves the numbers a file needs to be drawn,
 *     resolution and gamma, so the sentence names what goes; the browser's own pass
 *     in `lib/imageDownsize.ts` is a bandwidth optimisation and is stated here as not being the
 *     protection (§6). The capture time is drawn in `PhotoLightbox` and orders nothing.</li>
 *   <li><b>The oversight read is recorded and disclosed</b> — `ConversationAccessLog` is written
 *     in its own `SaveChanges` inside `EvaluateAccessAsync`, ahead of the read it pays for, and
 *     `ParticipantsPanel` renders "Read by oversight" with names to everybody in the thread, youth
 *     included (rule 7). `OneOversightRead` collapses repeat visits within fifteen minutes into one
 *     row, so the page says the thread shows the read rather than that every visit is its own
 *     record.</li>
 *   <li><b>What a person has been shown is theirs</b> (TROOP-268, rule 21e) — `SeenMarker` rows are
 *     read only by `SeenQueries`, whose every query has `s.PersonId == reader` in it, and no route
 *     takes another person; `SeenMarkerWriter` writes one only while `WhatIsNew` calls the item new,
 *     which is why the list items say <i>recent</i> announcements and <i>upcoming</i> events rather
 *     than everything opened. The young person's list also names read markers now: they had been
 *     recorded for children all along and the list had named them only for adults.</li>
 *   <li><b>No money</b> — there is no `decimal`, no `Amount` and no `Cost` anywhere in the domain;
 *     a payment requirement is free text a leader ticks (rule 18, §7).</li>
 *   <li><b>A young person's sign-in is recorded like anybody's</b> — `AuthSession` carries a user
 *     agent and an IP address whoever signs in, so the list of what is held about a child names it.
 *     The first draft said "nothing at all from the device they use", which read well, was checked
 *     against nothing, and was false for exactly the population this page exists for.</li>
 *   <li><b>What is actually deleted</b> — `StoredFile.PurgedAt` is rule 6's single exception, and
 *     the row deletions in the codebase are `PersonEmail` in three writers (a guardian clearing or
 *     replacing a child's, which revokes the link bound to it in the same transaction — rule 12a —
 *     and a leader correcting an adult's, which does not), `EventDuties` and the three notification
 *     preference tables. "Nothing is ever deleted" would have been the easy sentence and it is
 *     false; the paragraph below says which is which, and the population was counted with
 *     `grep -rn "\.Remove(\|RemoveRange("` rather than from rule 6's wording.</li>
 * </ul>
 *
 * <b>What is deliberately not claimed.</b> TROOP-128 (purging returned evidence at 24 months),
 * TROOP-151 (deleting an account in-app) and TROOP-204 (showing a guardian what is held about
 * their child) are all written up and none is built. Each is named here in the future tense with
 * the present-tense answer beside it — write to us and a person does it by hand — because a policy
 * is the one document where a planned control read as a live one is a misrepresentation rather
 * than an optimistic roadmap. <b>When any of the three lands, this page and
 * {@link PRIVACY_POLICY_VERSION} move in that ticket's own commit.</b>
 *
 * <b>The signed-out vocabulary rule applies here too</b> (`docs/frontend.md`). No entity name, no
 * policy class, no rule number reaches the rendered copy — those live in this comment, where the
 * contributor checking a claim can find them and the parent reading the page is not asked to. It
 * is the same reader `AboutScreen` is written for, with more at stake.
 *
 * <b>It must agree with three things, not two.</b> The code, the terms, and — when TROOP-152 lands
 * — the app-store privacy labels. A label contradicting this page is a store removal rather than a
 * rejection.
 *
 * <b>Every falsified sentence on this page was an absolute one, across two audits.</b> <i>No
 * third-party scripts of any kind</i> missed the Google loader; <i>the one time the app sends you
 * somewhere else</i> missed a requirement's link and then the blank form; <i>no other page loads
 * anything from anywhere else</i> missed the bucket every photograph is fetched from; <i>every
 * other detail a camera writes</i> missed the one field ingest deliberately keeps. Each was true of
 * the thing being described and false of the sentence's own scope, and each read as a stronger
 * disclosure than the accurate version — which is exactly why it was written that way.
 * <b>Prefer the sentence that names what it covers.</b> A quantifier here is a claim to have
 * enumerated something, and the enumeration is what the next reader will check.
 */
export function PrivacyScreen() {
  return (
    <LegalDocument title="Privacy policy" version={PRIVACY_POLICY_VERSION}>
      <LegalText>
        TroopHerald holds information about children, so this document says plainly what is held,
        who can see it, and what happens when something is deleted. Every claim in it was checked
        against what the product actually does. Where something is planned rather than built, it
        says so rather than describing it as though it were finished.
      </LegalText>

      <LegalSection title="Who operates TroopHerald">
        <LegalText>
          TroopHerald is operated by a sole proprietor based in Connecticut, in the United States.
          Write to <LegalAddress /> with anything in this document, including a request to see, correct
          or delete what is held about you or your child.
        </LegalText>
        <LegalText>
          Your unit runs its own program. TroopHerald is a tool the unit uses: the unit decides who
          joins it, who leads it, and what it records here. What the unit is responsible for is set
          out in the <LegalLink href="/terms">terms</LegalLink>.
        </LegalText>
      </LegalSection>

      <LegalSection title="The short version">
        <LegalList>
          <LegalItem>
            Nothing here is sold, and nothing is shared for advertising. There is no analytics
            service, no advertising and no tracking code anywhere in the product, and the typefaces
            are served from this site rather than from somebody else&rsquo;s network. The sign-in
            page is the single exception and it is described below.
          </LegalItem>
          <LegalItem>
            A young person&rsquo;s phone number is <LegalStrong>never collected</LegalStrong>. The
            form a leader fills in for a child has no field for one, and the routes that can carry a
            number will not write one onto a child&rsquo;s record.
          </LegalItem>
          <LegalItem>
            A young person&rsquo;s email address is visible to their guardians and to nobody else —
            not to leaders, not to the unit&rsquo;s administrators.
          </LegalItem>
          <LegalItem>
            Where a photograph was taken is destroyed as it is uploaded, before the photograph is
            stored.
          </LegalItem>
          <LegalItem>
            Nobody reads a unit&rsquo;s conversations invisibly. When an administrator opens a
            thread they are not part of, the thread says so, by name, to everybody in it —
            including the young people.
          </LegalItem>
          <LegalItem>
            No money passes through the product. It holds no card details, no bank details and no
            amounts.
          </LegalItem>
        </LegalList>
      </LegalSection>

      <LegalSection title="What is held about an adult">
        <LegalList>
          <LegalItem>Their name.</LegalItem>
          <LegalItem>The email addresses they sign in with.</LegalItem>
          <LegalItem>
            A contact phone number, if their unit records one. It is optional and it is typed by a
            leader, not collected from a device.
          </LegalItem>
          <LegalItem>
            Which units they belong to, what they do in each, and what they are allowed to do.
          </LegalItem>
          <LegalItem>
            What they write, upload, tick, answer and plan inside the product.
          </LegalItem>
          <LegalItem>
            Their sign-ins: when, a description of the browser, and the network address it came
            from — kept so that a session can be recognised and ended.
          </LegalItem>
          <LegalItem>Which messages they have read, and what they want to be told about.</LegalItem>
          <LegalItem>
            Which recent announcements and upcoming events they have been shown, so the app can
            stop marking them New.
          </LegalItem>
        </LegalList>
      </LegalSection>

      <LegalSection title="What is held about a young person">
        <LegalList>
          <LegalItem>Their name.</LegalItem>
          <LegalItem>
            Their date of birth. This is the most load-bearing fact in the product: it is what
            decides, on any given day, whether the rules treat them as a child or as an adult —
            which conversations they can be in, who may have their contact details, and whether
            they can sign in at all.
          </LegalItem>
          <LegalItem>
            An email address, once a guardian has entered one. Only a guardian can, and it is what
            the child&rsquo;s sign-in is made of.
          </LegalItem>
          <LegalItem>Who their guardians are, and over what period.</LegalItem>
          <LegalItem>
            What they write in the unit&rsquo;s conversations, whether they are coming to an event,
            what they are bringing, what they have completed, and the forms returned for them.
          </LegalItem>
          <LegalItem>Photographs the unit adds, which may be of them.</LegalItem>
          <LegalItem>
            Which messages they have read, and which recent announcements and upcoming events they
            have been shown, so the app can mark what is new to them.
          </LegalItem>
          <LegalItem>
            The same record of a sign-in as for an adult, once they have one: when, a description
            of the browser, and the network address it came from.
          </LegalItem>
        </LegalList>

        <LegalText>
          <LegalStrong>Not a phone number, ever.</LegalStrong> Not an address, not a school and not
          a medical history. Nothing is collected from the device beyond the sign-in record above —
          no location, no contacts, no identifier for advertising. If the unit&rsquo;s own paper
          form asks for any of that and a leader scans it in, see <em>Forms and files</em> below.
        </LegalText>
      </LegalSection>

      <LegalSection title="Guardians, and children under 13">
        <LegalText>
          A child cannot sign in to TroopHerald until one of their guardians has signed in first,
          and only a guardian can create the child&rsquo;s sign-up link. A leader cannot, however
          senior they are. Those two rules together mean a child&rsquo;s access to this product is
          set up by their own parent, deliberately, from their own account — which is both how the
          product was designed for safeguarding reasons and how a parent gives consent for what is
          described here.
        </LegalText>
        <LegalText>
          A guardian can read their child&rsquo;s conversations and act for them on their own
          things: answering for an event, ticking off what they are packing, returning a form. The
          one thing that does not extend is a job the child holds in the unit — a young person who
          keeps the unit&rsquo;s equipment register keeps it, and their guardian is not handed it
          because of the office.
        </LegalText>
        <LegalText>
          A guardian can ask for a copy of everything held about their child, ask for a correction,
          or ask for it to be deleted. Today that is an email to <LegalAddress /> and a person doing
          it by hand. A screen that shows a guardian this without their having to ask is being
          built; it does not exist yet, and this page will say so until it does.
        </LegalText>
      </LegalSection>

      <LegalSection title="Who can see what">
        <LegalText>
          Visibility is worked out by the product every time something is read, from the unit&rsquo;s
          own roster, and never from a copy of the answer made earlier. When the roster changes,
          what people can see changes with it. Knowing the address of a page is not access — the one
          deliberate exception is the calendar link described further down, which is a credential and
          is described as one.
        </LegalText>

        <LegalList>
          <LegalItem>
            A conversation is readable by the people in it, by their guardians, and by an
            administrator of that unit who asks for oversight — which is recorded and disclosed, as
            below.
          </LegalItem>
          <LegalItem>
            What a young person is packing belongs to them and to their guardians. A leader
            organising the trip sees how much of the list is ticked, not what is on it.
          </LegalItem>
          <LegalItem>
            A returned form, and anything scanned with it, is visible to the unit&rsquo;s adult
            leaders and administrators, to the person it is about, and to that child&rsquo;s
            guardians. Not to other families.
          </LegalItem>
          <LegalItem>
            A report about a message is private from the person it is about, visible to whoever
            raised it, and closed by an adult.
          </LegalItem>
          <LegalItem>
            Which announcements and events somebody has been shown is used only to mark what is new
            to them. No screen shows it to anybody else &mdash; not a leader, not an administrator,
            and not their guardian &mdash; and a guardian&rsquo;s own record is kept apart from
            their child&rsquo;s.
          </LegalItem>
        </LegalList>
      </LegalSection>

      <LegalSection title="Conversations, and the oversight read">
        <LegalText>
          A conversation with young people in it is held to the unit&rsquo;s two-adult rule by the
          product itself, not by whoever is paying attention.
        </LegalText>
        <LegalText>
          An administrator of a unit can read any conversation in that unit. It is the strongest
          permission in the product and the one most worth being honest about: it exists so that a
          unit can supervise what happens under its own name.{' '}
          <LegalStrong>Using it is recorded, and the thread shows it.</LegalStrong> The thread names
          the administrator who read it, to everybody who can read that thread, young people
          included; opening it again a few minutes later is the same record rather than a new one.
          There is no hidden reader, no participant list that leaves somebody out, and no way to
          look at a conversation without the people in it being told.
        </LegalText>
      </LegalSection>

      <LegalSection title="Photographs">
        <LegalText>
          Whether a unit uses photographs at all, and who in it may add one, is the unit&rsquo;s own
          decision.
        </LegalText>
        <LegalList>
          <LegalItem>
            <LegalStrong>Location is stripped on the way in</LegalStrong>, and so are the details a
            camera records about itself: the device, the settings, the serial number. The time the
            photograph was taken is kept, so it can be shown beside the picture. The stripping happens
            on our side, always. A photograph of a campsite otherwise carries the coordinates where a group
            of children slept.
          </LegalItem>
          <LegalItem>
            The store is never public. The web address of a photograph is short-lived, is issued
            only after checking who is asking, and never appears in an email.
          </LegalItem>
          <LegalItem>
            A photograph taken down stops being visible to everybody immediately, and a
            unit&rsquo;s administrator can go further and have the file itself destroyed. That one
            is genuinely irreversible.
          </LegalItem>
        </LegalList>
        <LegalText>
          <LegalStrong>The honest limit:</LegalStrong> nothing in the product knows who is in a
          photograph, so it cannot leave one child out of a gallery, and a setting that claimed to
          would be a promise it quietly broke. A family who does not want their child photographed
          should say so to the unit, which can take a photograph down or turn photographs off
          altogether.
        </LegalText>
      </LegalSection>

      <LegalSection title="Forms and files">
        <LegalText>
          A permission slip is the unit&rsquo;s own document, not ours. When a family or a leader
          scans one in, what is stored is whatever that unit chose to ask for on it — which can
          include health information.
        </LegalText>
        <LegalText>
          Beyond stripping a photograph&rsquo;s camera details and making a smaller copy to show on
          a phone, what is uploaded is not read, parsed or indexed by us. It is stored privately and
          handed back only to the people already entitled to it. Returned forms are kept until somebody in
          the unit removes them; purging them automatically once they are two years old is intended
          and is not built yet.
        </LegalText>
      </LegalSection>

      <LegalSection title="What is deleted, and what is kept">
        <LegalText>
          Most of this product does not erase things, and that is a deliberate safeguarding
          decision rather than an oversight:
        </LegalText>
        <LegalList>
          <LegalItem>
            A message taken down stops being visible and is <LegalStrong>kept</LegalStrong>, along
            with who took it down and why. A record that the person being reported can erase is not
            a record.
          </LegalItem>
          <LegalItem>
            An edited message keeps what it said before. Who was in a conversation and who has left
            it are both kept, because a list that quietly loses somebody has the same problem as
            one that quietly gains somebody.
          </LegalItem>
          <LegalItem>
            <LegalStrong>The contents of a stored file are the one exception.</LegalStrong> When a
            file is destroyed the bytes really do leave the store, and a row stays behind recording
            that a file was there and when it went.
          </LegalItem>
          <LegalItem>
            A few things do go for real, because keeping them would be worse: an email address that
            is replaced or cleared — and where a guardian clears their child&rsquo;s, the sign-up
            link bound to it is cancelled at the same instant, so a link already sitting in an inbox
            stops working — a notification preference that is switched off, and a job removed from
            an event&rsquo;s plan.
          </LegalItem>
        </LegalList>
        <LegalText>
          <LegalStrong>If you ask us to delete an account</LegalStrong>, what we can do is end its
          access, remove the contact details, and destroy any files. What we will not quietly do is
          remove a child&rsquo;s messages from their unit&rsquo;s own record of what was said, and a
          policy promising otherwise would be a promise not kept. Today this is an email to{' '}
          <LegalAddress /> and a person doing it by hand; doing it from inside the product is intended
          and is not built yet.
        </LegalText>
      </LegalSection>

      <LegalSection title="Signing in">
        <LegalText>
          Signing in is a six-digit code sent to your email address, or a Google account on that
          same address. There is no password to lose. A code lasts fifteen minutes, works once, and
          dies after five wrong guesses; only a scrambled copy of it is stored, so nobody can read
          your code out of the database.
        </LegalText>
        <LegalText>
          If you sign in with Google, what we receive is your email address, whether Google says it
          is verified, the name on the account, and an identifier for it. Nothing else — no access
          to your mail, your contacts or your files.
        </LegalText>
        <LegalText>
          A session is a row on our side rather than a token that has to expire on its own, so it
          can be ended rather than waited out. It lasts thirty days from when you last used it, and
          signing out ends it immediately. Ending a session on a device you no longer have is
          something to ask us to do today — a screen listing your own sessions is intended and is
          not built yet. The cookie that carries a session cannot be read by scripts, is sent only
          over a secure connection, and is not sent to other sites.
        </LegalText>
        <LegalSubheading>The calendar link</LegalSubheading>
        <LegalText>
          If you subscribe to your unit&rsquo;s calendar, the link you are given is the credential:
          anybody holding it can see that unit&rsquo;s events. It is shown once, and it can be
          revoked and reissued — which is the answer if you think somebody else has a copy.
        </LegalText>
      </LegalSection>

      <LegalSection title="Where it is held, and who else touches it">
        <LegalText>
          Data is stored and processed in the United States. Four companies handle it on our
          behalf, and this is the complete list:
        </LegalText>
        <LegalList>
          <LegalItem>
            <LegalStrong>Railway</LegalStrong> — runs the application and the database.
          </LegalItem>
          <LegalItem>
            <LegalStrong>Cloudflare</LegalStrong> — serves the website and stores uploaded files.
          </LegalItem>
          <LegalItem>
            <LegalStrong>Postmark</LegalStrong> — delivers the email the product sends: sign-in
            codes, invitations and the weekly summary.
          </LegalItem>
          <LegalItem>
            <LegalStrong>Google</LegalStrong> — if you sign in with a Google account, and see the
            sign-in page below.
          </LegalItem>
        </LegalList>
        <LegalText>
          None of them is an advertising relationship and none of them receives anything to use for
          their own purposes. There is no analytics service, no tracking and no advertising code in
          the product.
        </LegalText>
        <LegalText>
          <LegalStrong>The sign-in page is the one page that loads code from somebody else.</LegalStrong>{' '}
          Where signing in with Google is offered, that page fetches Google&rsquo;s sign-in script
          as it opens — before you have chosen how to sign in, and whichever way you then choose. So
          Google can tell that a browser opened the sign-in page, and where you are already signed
          in to Google in that browser it may be able to tell whose. The product sends nothing of
          its own with that request; what your browser sends is what it would send any script. No
          other page loads code from anywhere.
        </LegalText>
        <LegalText>
          <LegalStrong>Photographs and files come from Cloudflare&rsquo;s storage</LegalStrong>{' '}
          rather than from this site, over a web address that is short-lived and is issued only
          after we have checked who is asking. That is the company named above rather than a new
          one — but it is a different address in the browser, and opening a blank form, a
          document attached to an announcement or one on the unit&rsquo;s Info page goes there too.
        </LegalText>
        <LegalText>
          <LegalStrong>Email is not a secure channel</LegalStrong>, so the product does not put a
          photograph, a conversation or a roster in one. The web addresses it emails you all lead
          back into TroopHerald: a sign-in or sign-up link for your own account, and in the weekly
          summary, links to what is new and a link to stop receiving it.
        </LegalText>
        <LegalText>
          <LegalStrong>Four things send you to somebody else&rsquo;s site, and only when you tap
          them.</LegalStrong>{' '}
          Opening a place in a map hands that place&rsquo;s name and address to Apple or Google
          Maps, and nothing else. And a leader can link to another site — a council form, say —
          from an instruction, from an announcement or from the unit&rsquo;s Info page, either
          beneath the text or within it; each of those links shows you the site it goes to before
          you follow it. A web address written in a
          message, in a reply or in the details of a trip is text, not something to tap.
        </LegalText>
      </LegalSection>

      <LegalSection title="Changes to this policy">
        <LegalText>
          The version at the top of this page changes when what the policy says changes, so the
          document somebody was shown can always be identified afterwards. Recording which version
          a guardian was shown when they set their child up is what that identifier is for; it is
          intended and is not built yet. If something material changes, the unit&rsquo;s
          administrators are told by email.
        </LegalText>
      </LegalSection>

      <LegalSection title="Asking us something">
        <LegalText>
          Write to <LegalAddress />. You can ask for a copy of what is held about you or your child, ask
          for a correction, ask for it to be deleted, or ask for your unit&rsquo;s whole record to
          be handed over or removed. We answer within thirty days and usually the same week.
        </LegalText>
      </LegalSection>
    </LegalDocument>
  );
}

